import React, { useContext } from 'react';
import { EyeOutlined } from '@ant-design/icons';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-markdown';   // load more languages as needed
import styles from './HelpTab.module.css';
import useTranslation from '@common/hooks/useTranslation';
import { LogicMapperContext } from '@common/context/LogicMapperContext/LogicMapperContext';
import StyledButton from '@components/form-control/styledButton/styledButton';

type HelpTabProps = {
  /**
   * The markdown help content for the widget
   */
  helpMarkdown: string;
  /**
   * Callback for when the markdown content changes
   */
  onHelpMarkdownChange: (value: string) => void;
};

/**
 * Component for editing the markdown help content of a custom widget
 * @param props Component props
 * @returns React component
 */
const HelpTab = ({ helpMarkdown, onHelpMarkdownChange }: HelpTabProps): React.JSX.Element => {
  const t = useTranslation('CustomWidgetModal');
  const { onShowHelp } = useContext(LogicMapperContext);

  const highlight = (txt: string) => Prism.highlight(txt, Prism.languages.markdown, 'markdown');
  /**
   * Handle textarea changes
   * @param event Change event from textarea
   */
  const handleMarkdownChange = (value: string) => {
    onHelpMarkdownChange(value);
  };

  /**
   * Show markdown preview in help panel
   */
  const handlePreviewClick = () => {
    onShowHelp({
      widgetId: 'preview',
      recipeId: 'preview',
      thingRecipeId: 'preview',
      type: 'preview' as any,
      customMarkdown: helpMarkdown
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <p className={styles.description}>{t('Help.Description')}</p>
      </div>
      <div className={styles.EditorContainer}>
        <Editor
          value={helpMarkdown}
          className={styles.Editor}
          onValueChange={handleMarkdownChange}
          highlight={highlight}
          padding={12}
          style={{
            fontFamily: '"Fira Code", monospace',
            fontSize: 14,
            background: 'var(--kemu-surface-muted-bg)',
            color: 'var(--kemu-text-primary)',
          }}
        />
      </div>
      <div className={styles.PreviewContainer}>
        <StyledButton
          color='light'
          iconPosition='end'
          noShadow
          icon={<EyeOutlined />}
          onClick={handlePreviewClick}
          disabled={!helpMarkdown.trim()}
          title={t('Help.PreviewButton')}
        />
      </div>
    </div>
  );
};

export default HelpTab;
